import React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Stack from '@mui/joy/Stack';
import Sheet from '@mui/joy/Sheet';
import Checkbox from '@mui/joy/Checkbox';
import Divider from '@mui/joy/Divider';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Textarea from '@mui/joy/Textarea';
import {UserBlockRuleRow, getBlockRuleLabels, BlockRule} from '../../../models/userBlock';
import Chip from '@mui/joy/Chip';
import {userBlockRulesGetApi, userBlockRulesSetApi} from '../../userBlocks/userBlockRulesApi';
import {NotificationService} from '../../../common/notification';
import ConfirmDialog from '../../../components/confirmDialog/ConfirmDialog';
import CircularProgress from '@mui/joy/CircularProgress';
import { formatDateTime } from '../../../common/dateTime';

interface Props { userId: bigint }

export function UserBlocksTab({ userId }: Props) {
    const [data, setData] = React.useState<UserBlockRuleRow | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [editing, setEditing] = React.useState(false);
    const [ruleFlags, setRuleFlags] = React.useState<number>(0);
    const [comment, setComment] = React.useState<string>('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState<boolean>(false);

    const rules = getBlockRuleLabels();
    const allRulesMask = React.useMemo(() => rules.reduce((acc, r) => acc | r.value, 0), [rules]);

    React.useEffect(() => {
        load().catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const load = async () => {
        setLoading(true);
        try {
            const res = await userBlockRulesGetApi(userId);
            if (res.result) {
                const row = res.value ?? null;
                setData(row);
                setRuleFlags(row?.rule ?? 0);
                setComment(row?.comment ?? '');
            } else {
                NotificationService.alertError('Не удалось получить блокировку пользователя');
            }
        } finally {
            setLoading(false);
        }
    };

    const toggleEdit = () => setEditing(v => !v);

    const save = async () => {
        const res = await userBlockRulesSetApi(userId, ruleFlags, comment);
        if (res.result) {
            NotificationService.alertSuccess('Сохранено');
            setEditing(false);
            await load();
        } else {
            NotificationService.alertError('Не удалось сохранить');
        }
    };

    const remove = async () => {
        const res = await userBlockRulesSetApi(userId, BlockRule.None, '');
        if (res.result) {
            NotificationService.alertSuccess('Удалено');
            setEditing(false);
            await load();
        } else {
            NotificationService.alertError('Не удалось удалить');
        }
    };

    const hasBlock = !!data && data.rule !== BlockRule.None;

    return (
        <Box sx={{ width: '100%' }}>
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 8, minHeight: 140 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                        <CircularProgress size="lg" />
                    </Box>
                ) : !editing ? (
                    <Stack spacing={1}>
                        <Typography level="title-sm">Текущая блокировка</Typography>
                        {hasBlock ? (
                            <>
                                <Typography level="body-sm">Правила:</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {rules
                                        .filter(r => (data.rule & r.value) === r.value)
                                        .map(r => (
                                            <Chip key={r.value} size="sm" variant="soft" color="primary">{r.label}</Chip>
                                        ))}
                                </Box>
                                <Typography level="body-sm">Комментарий: {data.comment || '—'}</Typography>
                                <Typography level="body-xs" color="neutral">Создано: {formatDateTime(data.created)}</Typography>
                                <Typography level="body-xs" color="neutral">Обновлено: {formatDateTime(data.updated)}</Typography>
                            </>
                        ) : (
                            <Typography level="body-sm">Отсутсвует</Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            {!hasBlock ? (
                                <Button size="sm" onClick={() => { setRuleFlags(0); setComment(''); setEditing(true); }} disabled={loading}>Добавить</Button>
                            ) : (
                                <>
                                    <Button size="sm" onClick={toggleEdit} disabled={loading}>Редактировать</Button>
                                    <Button size="sm" color="danger" onClick={() => setDeleteConfirmOpen(true)} disabled={loading}>Удалить</Button>
                                </>
                            )}
                        </Stack>
                    </Stack>
                ) : (
                    <Stack spacing={1}>
                        <Typography level="title-sm">Редактирование</Typography>
                        <Checkbox
                            label={<Typography fontWeight="lg">Все</Typography>}
                            checked={(ruleFlags & allRulesMask) === allRulesMask && allRulesMask !== 0}
                            onChange={(e) => setRuleFlags(e.target.checked ? allRulesMask : 0)}
                        />
                        <Divider />
                        {/* Two-column groups for first two, then the rest */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                            {[{ title: 'Пополнение и Вывод', items: [BlockRule.Deposit, BlockRule.Withdraw] }, { title: 'Покупка и продажа', items: [BlockRule.Buy, BlockRule.Sell] }].map((grp, gi) => {
                                const mapped = grp.items.map(v => rules.find(r => r.value === v)!).filter(Boolean);
                                const groupMask = mapped.reduce((acc, it) => acc | it.value, 0);
                                const groupChecked = groupMask !== 0 && (ruleFlags & groupMask) === groupMask;
                                return (
                                    <Box key={gi}>
                                        <Checkbox
                                            size="sm"
                                            label={<Typography level="body-sm">{grp.title}</Typography>}
                                            checked={groupChecked}
                                            onChange={(e) => setRuleFlags(prev => e.target.checked ? (prev | groupMask) : (prev & ~groupMask))}
                                        />
                                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                            {mapped.map(r => (
                                                <Checkbox key={r.value} label={r.label} checked={(ruleFlags & r.value) === r.value} onChange={(e) => setRuleFlags(prev => e.target.checked ? (prev | r.value) : (prev & ~r.value))} />
                                            ))}
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Box>
                        {[{ title: 'Обмены', items: [BlockRule.ReceiveItems] }, { title: 'Страницы', items: [BlockRule.OperationsHistoryPage] }].map((grp, gi) => {
                            const mapped = grp.items.map(v => rules.find(r => r.value === v)!).filter(Boolean);
                            return (
                                <Box key={gi}>
                                    <Typography level="body-sm" sx={{ mb: 0.5 }}>{grp.title}</Typography>
                                    <Stack spacing={0.5}>
                                        {mapped.map(r => (
                                            <Checkbox key={r.value} label={r.label} checked={(ruleFlags & r.value) === r.value} onChange={(e) => setRuleFlags(prev => e.target.checked ? (prev | r.value) : (prev & ~r.value))} />
                                        ))}
                                    </Stack>
                                </Box>
                            );
                        })}
                        <Divider />
                        <FormControl>
                            <FormLabel>Комментарий</FormLabel>
                            <Textarea minRows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
                        </FormControl>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button variant="plain" onClick={toggleEdit}>Отмена</Button>
                            <Button onClick={save}>Сохранить</Button>
                        </Stack>
                    </Stack>
                )}
            </Sheet>
            <ConfirmDialog
                title="Подтверждение"
                open={deleteConfirmOpen}
                setOpen={setDeleteConfirmOpen as unknown as (e: boolean) => void}
                onConfirm={remove as unknown as () => void}
            >
                {`Вы действительно хотите удалить блокировку пользователя ${userId.toString()}?`}
            </ConfirmDialog>
        </Box>
    );
}


